import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import ObjectsIcon from '../../../assets/icons/annotations.svg?react';
import LayersIcon from '../../../assets/icons/layers.svg?react';
import { TabPane, TabsContainer } from '../../../components/Tabs';
import { T } from '../../../translation/src';
import { PointCloud, Project } from '../../../types/graphqlTypes';
import classNames from 'classnames';
import { memo, useCallback, useState } from 'react';
import useQueryParams from '../../../hooks/useQueryParams';
import { Annotations } from './Annotations';
import { useDeviceSize } from '../../../hooks/useDeviceSize';
import { Layers } from './Layers';

export enum LeftPanelTabs {
  OBJECTS = 'OBJECTS',
  LAYERS = 'LAYERS',
}

interface RendererLeftPanelProps {
  project?: Pick<Project, 'name' | 'description' | 'previewUrl' | 'settings' | 'id' | 'mapVisible'> & {
    pointClouds: Pick<PointCloud, 'cloudName' | 'id' | 'displayName' | 'uploadStatus' | 'visible' | 'availableClasses'>[];
  };
  closePanel: () => void;
  openPanel: () => void;
  panelOpen: boolean;
  hideArrow?: boolean;
  selectedTab?: LeftPanelTabs;
  onTabChange?: (tab: LeftPanelTabs) => void;
}
const RendererLeftPanel_: React.FC2<RendererLeftPanelProps> = ({ project, closePanel, openPanel, panelOpen, hideArrow, selectedTab: selectedTabProp, onTabChange }) => {
  const { annotationIdentifier: annotationIdParam = '' } = useQueryParams(['annotationIdentifier']);
  const [internalSelectedTab, setInternalSelectedTab] = useState<LeftPanelTabs>(LeftPanelTabs.OBJECTS);
  const { smDevice } = useDeviceSize();

  // Use controlled tab if provided, otherwise use internal state
  const selectedTab = selectedTabProp !== undefined ? selectedTabProp : internalSelectedTab;

  const onChangeTab = useCallback(
    ({ key }: { key: string }) => {
      if (!panelOpen) openPanel();
      const newTab = key as LeftPanelTabs;
      if (onTabChange) {
        onTabChange(newTab);
      } else {
        setInternalSelectedTab(newTab);
      }
    },
    [panelOpen, openPanel, onTabChange],
  );

  return (
    <div className="relative z-10 flex h-full">
      {!hideArrow && (
        <div className="absolute z-30 flex items-center justify-center w-8 h-8 transform -translate-y-1/2 rounded-full cursor-pointer text-neon-green-400 dark:text-black bg-eerie-black dark:bg-neon-green-300 top-1/2 -right-4">
          {panelOpen ? <ChevronLeftIcon className="w-4 h-4" onClick={closePanel} /> : <ChevronRightIcon className="w-4 h-4" onClick={openPanel} />}
        </div>
      )}
      <div
        data-panel="left"
        className={classNames(
          'relative flex flex-col flex-shrink-0 h-full px-3 py-3 bg-[rgb(230,230,230)] dark:bg-[rgb(33,33,33)] dark:text-white border-t border-gray-200 dark:border-transparent overflow-hidden',
          panelOpen ? (smDevice ? 'w-64' : 'w-96') : 'w-20',
        )}
      >
        {panelOpen && (
          <>
            {/* Project dropdown at the top */}
            {project && (
              <>
                <div className={'truncate text-center mb-2 w-full px-4 py-2 text-sm font-medium rounded-md dark:text-white'}>{project.name}</div>
                {/* Divider below project name */}
                <div className="my-2 w-full h-px bg-gray-300 dark:bg-gray-600" />
              </>
            )}

            <TabsContainer onChange={onChangeTab} defaultActiveKey={selectedTab || LeftPanelTabs.OBJECTS} tabWidth={smDevice ? '7.66rem' : '11.25rem'}>
              <TabPane key={LeftPanelTabs.OBJECTS}>
                <div className="inline-flex items-center space-x-2 select-none">
                  <ObjectsIcon />{' '}
                  <div>
                    <T _str="objects" swc />
                  </div>
                </div>
              </TabPane>
              <TabPane key={LeftPanelTabs.LAYERS}>
                <div className="inline-flex items-center space-x-2 select-none">
                  <LayersIcon />{' '}
                  <div>
                    <T _str="layers" swc />
                  </div>
                </div>
              </TabPane>
            </TabsContainer>
            {selectedTab === LeftPanelTabs.OBJECTS && project && <Annotations project={project} defaultSelectedAnnotationId={annotationIdParam} />}
            {selectedTab === LeftPanelTabs.LAYERS && <Layers project={project} />}
          </>
        )}
        {!panelOpen && (
          <div className="flex flex-col w-full h-full">
            <div
              className={classNames('flex items-center justify-center cursor-pointer p-4 rounded-lg', selectedTab === LeftPanelTabs.OBJECTS && 'bg-neon-green-300 dark:bg-black')}
              onClick={() => onChangeTab({ key: LeftPanelTabs.OBJECTS })}
            >
              <ObjectsIcon />
            </div>
            <div className={classNames('flex items-center justify-center cursor-pointer p-4 rounded-lg', selectedTab === LeftPanelTabs.LAYERS && 'bg-neon-green-300 dark:bg-black')} onClick={() => onChangeTab({ key: LeftPanelTabs.LAYERS })}>
              <LayersIcon />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const RendererLeftPanel = memo(RendererLeftPanel_);
